import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import {
  canClientMoveToBacklog,
  isBacklogTicketStatus,
  ticketDisplaySubject,
  type TicketAttachment,
  type TicketDetail,
  type TicketMessage,
} from '@t3/shared';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card, StatusBadge } from '../components/Card.tsx';
import { EmailBody } from '../components/EmailBody.tsx';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalHealthBanner } from '../components/PortalHealthBanner.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';
import { Textarea } from '../components/Textarea.tsx';
import { ProjectProgressBar } from '../components/ProjectProgressBar.tsx';

interface PortalContact {
  id: string;
  organizationId: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

interface ProjectInfo {
  id: string;
  name: string;
  billingMode: 'flat_fee' | 'block_hours';
  budgetedHours: number;
  consumedMinutes: number;
  /** Task progress, 0-100 - the same figure as the project page. */
  progressPercent: number;
  subtaskTotal: number;
  subtaskCompleted: number;
}

interface DetailResponse {
  ticket: TicketDetail;
  messages: TicketMessage[];
  attachments: TicketAttachment[];
  assignedAgentBookingUrl: string | null;
  project: ProjectInfo | null;
}

export function PortalTicketDetailPage(): React.ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [contact, setContact] = useState<PortalContact | null>(null);
  const [data, setData] = useState<DetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reply, setReply] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [backlogBusy, setBacklogBusy] = useState(false);
  const [backlogError, setBacklogError] = useState<string | null>(null);

  async function load() {
    try {
      const res = await apiFetch<DetailResponse>(`/api/portal/tickets/${id}`);
      setData(res);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load ticket.');
    }
  }

  useEffect(() => {
    apiFetch<{ contact: PortalContact | null }>('/api/portal/auth/me')
      .then((r) => {
        if (!r.contact) {
          navigate('/login', { replace: true, state: { from: `/portal/tickets/${id}` } });
          return;
        }
        setContact(r.contact);
      })
      .catch(() => {
        navigate('/login', { replace: true, state: { from: `/portal/tickets/${id}` } });
      });
    void load();
  }, [id, navigate]);

  async function postReply(e: React.FormEvent) {
    e.preventDefault();
    setReplyError(null);
    setBusy(true);
    try {
      if (files.length > 0) {
        const fd = new FormData();
        fd.set('bodyHtml', reply);
        for (const f of files) fd.append('files', f, f.name);
        await apiFetch(`/api/portal/tickets/${id}/messages`, { method: 'POST', body: fd });
      } else {
        await apiFetch(`/api/portal/tickets/${id}/messages`, {
          method: 'POST',
          json: { bodyHtml: reply },
        });
      }
      setReply('');
      setFiles([]);
      await load();
    } catch (e) {
      setReplyError(e instanceof ApiError ? e.message : 'Failed to post reply.');
    } finally {
      setBusy(false);
    }
  }

  async function setBacklog(backlog: boolean) {
    setBacklogError(null);
    setBacklogBusy(true);
    try {
      await apiFetch(`/api/portal/tickets/${id}/backlog`, { method: 'PUT', json: { backlog } });
      await load();
    } catch (e) {
      setBacklogError(e instanceof ApiError ? e.message : 'Failed to update the ticket.');
    } finally {
      setBacklogBusy(false);
    }
  }

  if (error)
    return (
      <div className="bg-white">
        <PortalHealthBanner />
        <PortalNavBar contact={contact} active="tickets" />
        <main className="mx-auto max-w-6xl px-6 py-10 sm:px-8">
          <p className="text-sm text-[var(--color-danger)]">{error}</p>
          <p className="mt-2">
            <Link to="/portal/tickets" className="text-sm text-[var(--color-t3-blue)] hover:underline">
              ← Back to my tickets
            </Link>
          </p>
        </main>
      </div>
    );
  if (!data)
    return (
      <div className="bg-white">
        <PortalHealthBanner />
        <PortalNavBar contact={contact} active="tickets" />
        <main className="mx-auto max-w-6xl px-6 py-10 sm:px-8">
          <p className="text-[var(--color-muted)]">Loading…</p>
        </main>
      </div>
    );

  const { ticket, messages, attachments, assignedAgentBookingUrl, project } = data;
  const closed = ['solved', 'cancelled', 'dead'].includes(ticket.status);
  const inBacklog = isBacklogTicketStatus(ticket.status);
  const canBacklog = canClientMoveToBacklog(ticket.type, ticket.status);

  return (
    <div className="bg-white">
      <PortalHealthBanner />
      <PortalNavBar contact={contact} active="tickets" />
      <main className="mx-auto max-w-6xl space-y-6 px-6 py-10 sm:px-8">
      <div>
        <Link
          to="/portal/tickets"
          className="text-sm text-[var(--color-muted)] hover:underline"
        >
          ← My tickets
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <span className="font-mono text-sm text-[var(--color-muted)]">T-{ticket.displayNumber}</span>
          <StatusBadge status={ticket.status}>{ticket.status.replace(/_/g, ' ')}</StatusBadge>
          {canBacklog && (
            <Button
              variant="secondary"
              disabled={backlogBusy}
              onClick={() => void setBacklog(true)}
            >
              {backlogBusy ? 'Moving…' : 'Move to backlog'}
            </Button>
          )}
        </div>
        <h1 className="mt-2 text-2xl font-bold text-[var(--color-header)]">
          {ticketDisplaySubject(ticket.clientTicketNumber, ticket.subject)}
        </h1>
        {assignedAgentBookingUrl && !closed && (
          <div className="mt-3">
            <a
              href={assignedAgentBookingUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button variant="secondary">📅 Schedule a session</Button>
            </a>
            <span className="ml-2 text-xs text-[var(--color-muted)]">
              Opens {ticket.assignedAgentName ?? 'your technician'}'s booking page in a new tab.
            </span>
          </div>
        )}
        {inBacklog && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-[var(--radius-card)] border border-violet-200 bg-violet-50 p-4">
            <p className="text-sm text-violet-900">
              <span className="font-semibold">This is in your backlog.</span> We won't start work on it
              until you move it out. Replies here won't take it out of the backlog.
            </p>
            <Button disabled={backlogBusy} onClick={() => void setBacklog(false)}>
              {backlogBusy ? 'Moving…' : 'Move out of backlog'}
            </Button>
          </div>
        )}
        {backlogError && <p className="mt-2 text-sm text-[var(--color-danger)]">{backlogError}</p>}
      </div>

      {/* Progress is the task list, ticked off by the team - the same
          number as the project page. Hours are not progress: a job can
          burn half its budget on the first milestone. Projects with no
          task list keep the hours view below. */}
      {project && (project.subtaskTotal ?? 0) > 0 && (
        <Card>
          <h2 className="mb-3 text-base font-semibold text-[var(--color-header)]">
            Project:{' '}
            <Link
              to={`/portal/projects/${project.id}`}
              className="text-[var(--color-t3-blue)] hover:underline"
            >
              {project.name}
            </Link>
          </h2>
          <ProjectProgressBar
            percent={project.progressPercent}
            done={project.subtaskCompleted}
            total={project.subtaskTotal}
          />
          <Link
            to={`/portal/projects/${project.id}`}
            className="mt-3 inline-block text-sm font-semibold text-[var(--color-t3-blue)] hover:underline"
          >
            See the task list →
          </Link>
        </Card>
      )}

      {project && !((project.subtaskTotal ?? 0) > 0) && (
        <Card>
          <div className="flex items-baseline justify-between">
            <h2 className="text-base font-semibold text-[var(--color-header)]">
              Project:{' '}
              <Link
                to={`/portal/projects/${project.id}`}
                className="text-[var(--color-t3-blue)] hover:underline"
              >
                {project.name}
              </Link>
            </h2>
            {project.billingMode === 'flat_fee' ? (
              <span className="text-sm font-semibold text-[var(--color-header)]">
                {project.budgetedHours > 0
                  ? Math.min(100, Math.round((project.consumedMinutes / 60 / project.budgetedHours) * 100))
                  : 0}
                % complete
              </span>
            ) : (
              <span className="text-sm text-[var(--color-muted)]">
                {(project.consumedMinutes / 60).toFixed(2)}h logged
              </span>
            )}
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-[var(--color-border)]">
            <div
              className="h-full bg-[var(--color-t3-blue)] transition-all"
              style={{
                width: `${
                  project.budgetedHours > 0
                    ? Math.min(100, (project.consumedMinutes / 60 / project.budgetedHours) * 100)
                    : 0
                }%`,
              }}
            />
          </div>
          {project.billingMode === 'flat_fee' && (
            <p className="mt-2 text-xs text-[var(--color-muted)]">
              This project is billed at a flat fee. Hours are tracked internally to gauge progress.
            </p>
          )}
        </Card>
      )}

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Original message</h2>
        <div
          className="mt-2 whitespace-pre-wrap text-sm text-[var(--color-body)]"
          dangerouslySetInnerHTML={{ __html: ticket.descriptionHtml || '<em>No description</em>' }}
        />
      </Card>

      <div>
        <h2 className="mb-3 text-base font-semibold text-[var(--color-header)]">Conversation</h2>
        <div className="space-y-3">
          {messages.length === 0 ? (
            <Card>
              <p className="text-center text-[var(--color-muted)]">No replies yet.</p>
            </Card>
          ) : (
            messages.map((m) => {
              const msgAtt = attachments.filter((a) => a.messageId === m.id);
              return (
                <Card key={m.id}>
                  <div className="mb-2 flex items-center justify-between text-xs text-[var(--color-muted)]">
                    <span>
                      <span className="font-semibold text-[var(--color-header)]">
                        {m.authorName ?? m.authorType}
                      </span>{' '}
                      · {m.authorType}
                    </span>
                    <span>{new Date(m.createdAt).toLocaleString()}</span>
                  </div>
                  <EmailBody html={m.bodyHtml} />
                  {msgAtt.length > 0 && (
                    <ul className="mt-3 flex flex-wrap gap-2 border-t border-[var(--color-border)] pt-3 text-xs">
                      {msgAtt.map((a) => (
                        <li key={a.id}>
                          <a
                            href={`/api/portal/tickets/${ticket.id}/attachments/${a.id}`}
                            className="inline-flex items-center gap-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-2 py-1 text-[var(--color-t3-blue)] hover:bg-[var(--color-surface)]"
                            download={a.filename}
                          >
                            📎 {a.filename}
                            <span className="text-[var(--color-muted)]">
                              ({Math.ceil(a.sizeBytes / 1024)} KB)
                            </span>
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </Card>
              );
            })
          )}
        </div>
      </div>

      {!closed && (
        <Card>
          <h2 className="text-base font-semibold text-[var(--color-header)]">Reply</h2>
          <form onSubmit={postReply} className="mt-3 space-y-3">
            <Textarea
              label="Message"
              name="reply"
              rows={4}
              required
              value={reply}
              onChange={(e) => setReply(e.target.value)}
            />
            <div>
              <label className="block text-sm font-medium text-[var(--color-header)]">
                Attachments (optional, ≤25MB each)
              </label>
              <input
                type="file"
                multiple
                onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
                className="mt-1 block w-full text-sm text-[var(--color-body)] file:mr-3 file:rounded-[var(--radius-button)] file:border-0 file:bg-[var(--color-t3-blue)] file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-white hover:file:bg-[var(--color-t3-blue)]/90"
              />
              {files.length > 0 && (
                <ul className="mt-2 text-xs text-[var(--color-muted)]">
                  {files.map((f, i) => (
                    <li key={i}>📎 {f.name}</li>
                  ))}
                </ul>
              )}
            </div>
            {replyError && <p className="text-sm text-[var(--color-danger)]">{replyError}</p>}
            <Button type="submit" disabled={busy || !reply.trim()}>
              {busy ? 'Posting…' : 'Send reply'}
            </Button>
          </form>
        </Card>
      )}

      {closed && (
        <Card>
          <p className="text-sm text-[var(--color-muted)]">
            This ticket is closed. <Link to="/portal/tickets/new" className="text-[var(--color-t3-blue)] hover:underline">Submit a new ticket</Link> if you need help.
          </p>
        </Card>
      )}
      </main>
      <PortalFooter />
    </div>
  );
}
